import { useCallback, useSyncExternalStore } from 'react';
import { getInlineManifest } from './state';

/**
 * Providers whose profile page exists as a prerendered file (built by scripts/prerender.mjs, file
 * `provider-manifest.{hash}.json`). A public card renders `<a href>` only for these providers: any other link
 * would reach a URL without a file and be answered 403 by the static origin.
 *
 * - Server (build): the full list, set before the first page is rendered; the ids a render asked about are
 *   recorded so the build serializes exactly those into the page state.
 * - Client: starts from the ids serialized in the page (same answers as the HTML), then is replaced by the latest
 *   manifest (services/providerManifest.service.ts), so an old cached page never keeps a link to a removed profile.
 */
export interface ProviderManifest {
  hash: string;
  ids: readonly string[];
  /** Only the ids the prerendered page asked about (serialized in the page): the full list must still be fetched. */
  partial?: boolean;
}

interface Current {
  hash: string;
  partial: boolean;
  ids: ReadonlySet<string>;
}

let current: Current | null | undefined;
const listeners = new Set<() => void>();
export const serverManifestUsage = { queried: new Set<string>() };

const isServer = () => typeof window === 'undefined';
const toCurrent = (m: ProviderManifest | null | undefined): Current | null => (m ? { hash: m.hash, partial: m.partial === true, ids: new Set(m.ids) } : null);

const read = (): Current | null => {
  if (current === undefined) current = toCurrent(getInlineManifest());
  return current;
};

export const setProviderManifest = (manifest: ProviderManifest | null): void => {
  current = toCurrent(manifest);
  listeners.forEach((l) => l());
};

/** Hash of the FULL manifest held, or null when none or only a partial (page-embedded) one: then always fetch. */
export const getManifestHash = (): string | null => {
  const m = read();
  return m && !m.partial ? m.hash : null;
};

export const resetManifestUsage = (): void => serverManifestUsage.queried.clear();

/** Is there a prerendered profile page for this provider? Unknown manifest means no link. */
export const hasProfilePage = (providerId: string): boolean => {
  if (isServer()) serverManifestUsage.queried.add(providerId);
  return read()?.ids.has(providerId) ?? false;
};

const subscribe = (listener: () => void): (() => void) => {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
};

export const useHasProfilePage = (providerId: string): boolean => {
  const snapshot = useCallback(() => hasProfilePage(providerId), [providerId]);
  return useSyncExternalStore(subscribe, snapshot, snapshot);
};
